import React, { useReducer, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import deburr from 'lodash/deburr';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import Downshift from 'downshift';
import Popper from '@material-ui/core/Popper';
import TextField from '@material-ui/core/TextField';
import Card from '@material-ui/core/Card';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import { colors } from '@lavu-pos/stellar/dist/themes';
import { withStyles } from '@material-ui/styles';

const styles = {
	container: {
		position: 'relative',
	},
	popper: {
		zIndex: 1900,
	},
	paper: {
		marginTop: 5,
		maxHeight: 200,
		overflowY: 'auto',
	},
	inputInput: {
		fontSize: 14,
		lineHeight: 1.5,
		paddingTop: 9.5,
		paddingBottom: 9.5,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	inputDisabled: {
		opacity: '2 !important',
		color: colors.disabledGray,
		cursor: 'not-allowed',
	},
	iconButton: {
		padding: 2.5,
	},
	menuItem: {
		whiteSpace: 'unset',
		fontWeight: 600,
	},
	menuItemSelected: {
		fontWeight: 'bold',
	},
};
const ITEM_HEIGHT = 46;

const initialState = {
	open: false,
	inputValue: '',
	highlightedIndex: 0,
	selectedItem: null,
};

const handleReducer = (state, action) => {
	switch (action.type) {
		case 'open':
			return { ...state, open: action.open };
		case 'input':
			return { ...state, inputValue: action.inputValue };
		case 'select':
			return { ...state, open: false, ...action.payload };
		default:
			return state;
	}
};

const handleStateChange = (changes, options, inputRef) => {
	if (changes.type === Downshift.stateChangeTypes.changeInput) {
		return { type: 'input', inputValue: changes.inputValue };
	} else if (
		changes.type === Downshift.stateChangeTypes.clickItem &&
		changes.selectedItem
	) {
		const highlightedIndex =
			findIndex(options, { label: changes.selectedItem }) || 0;
		inputRef.current.blur();
		return {
			type: 'select',
			payload: {
				selectedItem: changes.selectedItem,
				inputValue: changes.selectedItem,
				highlightedIndex,
			},
		};
	} else if (
		changes.type === Downshift.stateChangeTypes.unknown &&
		changes.hasOwnProperty('isOpen')
	) {
		return {
			type: 'open',
			open: changes.isOpen,
		};
	}
	return {
		type: 'default',
	};
};

const AutoComplete = ({
	elementIdPrefix,
	name,
	placeholder,
	value,
	options,
	onChange,
	disabled,
	error,
	classes,
}) => {
	const [state, dispatch] = useReducer(handleReducer, initialState);
	const { open, highlightedIndex, inputValue, selectedItem } = state;
	const inputRef = useRef(null);
	const anchorEl = useRef(null);

	useEffect(() => {
		if (!isEmpty(options) && value) {
			const { label } = find(options, { value }) || {};
			const highlightedIndex = findIndex(options, { value });
			dispatch({
				type: 'select',
				payload: {
					selectedItem: label,
					inputValue: label,
					highlightedIndex,
				},
			});
		}
	}, [value, options]);

	const onStateChange = changes =>
		dispatch(handleStateChange(changes, options, inputRef));

	const buildOptions = useCallback(() => {
		const searchValue = deburr(inputValue).toLowerCase();
		const inputLength = searchValue.length;
		return !inputLength || find(options, { label: inputValue })
			? options
			: options.filter(option =>
					option.label.toLowerCase().includes(searchValue)
			  );
	}, [options, inputValue]);

	const elementId = elementIdPrefix
		? `${elementIdPrefix}-${name}`
		: 'downshift';

	/**
	 * @const
	 *
	 * Defines the custom event handlers we're planning to pass to the downshift input field
	 */

	const customEventHandlers = {
		onClick: () => dispatch({ type: 'open', open: true }),
		onChange: () => dispatch({ type: 'open', open: true }),
		onFocus: () => dispatch({ type: 'open', open: true }),
	};

	/**
	 * @function
	 * Returns the custom Props we want to set on the input field
	 * Applies the disabled status to the event handlers as Downshift only handles
	 * onChangeKey, onKeyDown, and onBlur as per
	 * (https://github.com/downshift-js/downshift/blob/master/src/downshift.js#L798)
	 *
	 * @returns {object} Object should contain the custom event handlers and should
	 * pass the disabled status to the MUI input that is used by "StyledTexField"
	 */
	const customInputProps = () => {
		const handlers = disabled ? {} : customEventHandlers;
		return {
			disabled,
			...handlers,
		};
	};

	useEffect(() => {
		if (open) {
			const menu = document.querySelector('.downshift-autocomplete-menu');
			if (menu) {
				const indexToScroll = findIndex(buildOptions(), {
					label: selectedItem,
				});

				menu.scrollTop = Math.max(+indexToScroll - 1, 0) * ITEM_HEIGHT;
			}
		}
	});

	return (
		<div id={`${elementId}-root`} ref={anchorEl}>
			<Downshift
				id={`${elementId}-downshift-component`}
				isOpen={open}
				selectedItem={selectedItem}
				inputValue={inputValue}
				highlightedIndex={highlightedIndex}
				onStateChange={onStateChange}
				onOuterClick={() => dispatch({ type: 'open', open: false })}
				itemToString={item => (item ? item.value : '')}
				onSelect={selectedItem => {
					const { value } = find(options, {
						label: selectedItem,
					});
					onChange(name, value);
				}}
			>
				{({ getInputProps, getItemProps, getMenuProps }) => {
					const {
						onBlur,
						onClick,
						onChange,
						onFocus,
						...inputProps
					} = getInputProps(customInputProps());
					return (
						<div className={classes.container}>
							<TextField
								name={name}
								error={Boolean(error)}
								fullWidth
								variant="filled"
								margin="none"
								placeholder={placeholder}
								disabled={disabled}
								InputProps={{
									classes: {
										input: classes.inputInput,
										disabled: classes.inputDisabled,
									},
									onBlur,
									onClick,
									onChange,
									onFocus,
									endAdornment: (
										<InputAdornment position="end">
											<IconButton className={classes.iconButton}>
												<ArrowDropDown />
											</IconButton>
										</InputAdornment>
									),
								}}
								inputRef={inputRef}
								{...inputProps}
							/>
							<Popper
								id={`${elementId}-popper`}
								open={open}
								key={inputValue}
								className={`downshift-autocomplete-popper ${classes.popper}`}
								anchorEl={anchorEl.current}
							>
								<div
									{...(open
										? getMenuProps({}, { suppressRefError: true })
										: {})}
								>
									<Card
										id={`${elementId}-paper`}
										className={`downshift-autocomplete-menu ${classes.paper}`}
										style={{
											width: anchorEl.current && anchorEl.current.clientWidth,
										}}
									>
										{buildOptions().map(({ id, key, label }, index) => (
											<MenuItem
												classes={{
													root: classes.menuItem,
													selected: classes.menuItemSelected,
												}}
												key={`${key}-${id}`}
												selected={highlightedIndex === index}
												component="div"
												data-option-index={index}
												{...getItemProps({
													id: id || `${elementId}-item-${index}`,
													item: label,
												})}
											>
												{label}
											</MenuItem>
										))}
									</Card>
								</div>
							</Popper>
						</div>
					);
				}}
			</Downshift>
		</div>
	);
};

AutoComplete.propTypes = {
	elementIdPrefix: PropTypes.string,
	name: PropTypes.string,
	placeholder: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			label: PropTypes.any,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		})
	),
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func,
	classes: PropTypes.object,
	disabled: PropTypes.bool,
	error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

AutoComplete.defaultProps = {
	onChange: Function.prototype,
	options: Object.prototype,
};

export default withStyles(styles)(AutoComplete);
